/**
 * 키 입력. 프레임마다 입력 스냅샷을 만들어 엔티티에 전달한다.
 * 폰은 `touchPad` 홀드/원샷을 같은 스냅샷에 합친다.
 * 키 배치는 `Settings` 를 따른다.
 */

import Phaser from 'phaser';
import { touchPad } from '../platform/TouchPad';
import { BIND_ORDER, eventToBindName, loadSettings, settings, isSettingsCapturing, type BindAction } from '../systems/Settings';

export interface InputSnapshot {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  jumpPressed: boolean;
  attackPressed: boolean;
  skill1Pressed: boolean;
  skill2Pressed: boolean;
  skill3Pressed: boolean;
  lootPressed: boolean;
  upPressed: boolean;
  downPressed: boolean;
}

type OneShot = 'jump' | 'attack' | 'skill1' | 'skill2' | 'skill3' | 'loot' | 'up' | 'down';

const HELD = new Set<BindAction>();
const PRESSED = new Set<OneShot>();
let listening = false;

function actionForBind(name: string): BindAction | undefined {
  const b = settings().binds;
  for (const act of BIND_ORDER) {
    if (b[act].includes(name)) return act;
  }
  return undefined;
}

function ensureListen(): void {
  if (listening) return;
  listening = true;
  loadSettings();
  window.addEventListener(
    'keydown',
    (ev) => {
      if (ev.repeat) return;
      if (isSettingsCapturing()) return;
      if (document.querySelector('#map-dock:not([hidden])')) return;
      const name = eventToBindName(ev);
      if (!name) return;
      const act = actionForBind(name);
      if (!act) return;
      HELD.add(act);
      if (act === 'jump' || act === 'attack' || act === 'skill1' || act === 'skill2' || act === 'skill3' || act === 'loot' || act === 'up' || act === 'down') {
        PRESSED.add(act);
      }
    },
    { capture: true },
  );
  window.addEventListener(
    'keyup',
    (ev) => {
      const name = eventToBindName(ev);
      if (!name) return;
      const act = actionForBind(name);
      if (act) HELD.delete(act);
    },
    { capture: true },
  );
}

export class Controls {
  constructor(scene: Phaser.Scene) {
    ensureListen();
    const kb = scene.input.keyboard;
    if (!kb) return;
    kb.addCapture([
      Phaser.Input.Keyboard.KeyCodes.LEFT,
      Phaser.Input.Keyboard.KeyCodes.RIGHT,
      Phaser.Input.Keyboard.KeyCodes.UP,
      Phaser.Input.Keyboard.KeyCodes.DOWN,
      Phaser.Input.Keyboard.KeyCodes.SPACE,
      Phaser.Input.Keyboard.KeyCodes.ALT,
      Phaser.Input.Keyboard.KeyCodes.CTRL,
      Phaser.Input.Keyboard.KeyCodes.SHIFT,
      Phaser.Input.Keyboard.KeyCodes.Z,
      Phaser.Input.Keyboard.KeyCodes.A,
      Phaser.Input.Keyboard.KeyCodes.F,
    ]);
  }

  snapshot(): InputSnapshot {
    const empty: InputSnapshot = {
      left: false,
      right: false,
      up: false,
      down: false,
      jumpPressed: false,
      attackPressed: false,
      skill1Pressed: false,
      skill2Pressed: false,
      skill3Pressed: false,
      lootPressed: false,
      upPressed: false,
      downPressed: false,
    };
    if (
      document.querySelector(
        '#chat-dock:not([hidden]), #auth-dock:not([hidden]), #friend-dock:not([hidden]), #char-dock:not([hidden]), #map-dock:not([hidden])',
      )
    ) {
      PRESSED.clear();
      touchPad.shots.clear();
      return empty;
    }
    const pad = touchPad.consumeShots();
    const snap: InputSnapshot = {
      left: HELD.has('left') || touchPad.held.has('left'),
      right: HELD.has('right') || touchPad.held.has('right'),
      up: HELD.has('up') || touchPad.held.has('up'),
      down: HELD.has('down') || touchPad.held.has('down'),
      jumpPressed: PRESSED.has('jump') || pad.has('jump'),
      attackPressed: PRESSED.has('attack') || pad.has('attack'),
      skill1Pressed: PRESSED.has('skill1') || pad.has('skill1'),
      skill2Pressed: PRESSED.has('skill2') || pad.has('skill2'),
      skill3Pressed: PRESSED.has('skill3') || pad.has('skill3'),
      lootPressed: PRESSED.has('loot') || pad.has('loot'),
      upPressed: PRESSED.has('up') || pad.has('up'),
      downPressed: PRESSED.has('down') || pad.has('down'),
    };
    PRESSED.clear();
    return snap;
  }
}
